import { Injectable } from "@angular/core";
import { BasePricingService } from "./BasePricingService";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OrderDataPrimaryLookupService } from "../../model/impl/OrderDataPrimaryLookupService";
import { NotificationService } from "../../notification/impl/NotificationService";
import { OrderDataImpl, OrderState, OrderType } from "../../../../cubiq-model-multi-asset";
import { Rate } from "../../../rate/model/Rate";
import { Temperature } from "../../../model/Temperature";
import { SystemConfigPrimaryService } from "shared/auth-services";
import { SysFeatures } from "../../../model/core/SysFeatures";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXNotificationEventType } from "../../../model/core/notifications/BFXNotificationEventType";
import { PercentAwayHelper } from "../../../model/orders/PercentAwayHelper";
import { CcyPairLookupService } from "../../model/impl/CcyPairLookupService";
import { ProfilePromiseActionService } from "../../../profile/impl/ProfilePromiseActionService";
import { RatePromiseService } from "shared/cubiq-services/rate/impl/RatePromiseService";
import { OrderDataLookupService } from "../../model/impl/OrderDataLookupService";
import { OrderLifecycleRateSubscriptionSource } from "shared/cubiq-services/rate/impl/OrderLifecycleRateSubscriptionSource";
import { Subscription } from "rxjs";

@Injectable()
export class OrderTemperatureService {
  protected orderTempCoolOffSecs: number = 900; // 15mins default, will need to read a sys config
  protected orderTempMap: Map<string, { temp: Temperature; lastHot: number }> = new Map();
  protected percentageAwayHelper: PercentAwayHelper = null;
  protected notificationEnabled: boolean = false;

  private orderUpdateSubscription: Subscription = null;
  private orderDeleteSubscription: Subscription = null;
  private rateUpdateSubscription: Subscription = null;

  constructor(
    protected pricingService: BasePricingService,
    protected rateService: RatePromiseService,
    protected orderDataService: OrderDataPrimaryLookupService,
    protected notifService: NotificationService,
    protected sysConfigService: SystemConfigPrimaryService,
    protected profileService: ProfilePromiseActionService,
    protected ccyPairLookupService: CcyPairLookupService,
    protected orderLifecycleRateSubscriptionSource: OrderLifecycleRateSubscriptionSource,
  ) {
    this.sysConfigService.getSystemConfig(SysFeatures.hotNotifCoolDownSecs).then((values) => {
      this.setCoolOffTime(values);
    });

    this.checkNotificationEnabled();
    this.notifService.notificationUpdateSubject.subscribe(this.checkNotificationEnabled.bind(this));

    this.percentageAwayHelper = new PercentAwayHelper(
      this.pricingService,
      this.profileService,
      this.ccyPairLookupService,
    );
  }

  protected setCoolOffTime(value: any): void {
    try {
      if (value) {
        this.orderTempCoolOffSecs = Number(value);
      }
    } catch (e) {
      logger.out.error(
        this.constructor.name +
          ": Failed to read system config " +
          SysFeatures.hotNotifCoolDownSecs +
          ", defaulting to " +
          this.orderTempCoolOffSecs +
          " value.",
      );
    }
  }

  protected initialize(): void {
    Promise.all([
      this.sysConfigService.getSystemConfig(SysFeatures.hotNotifCoolDownSecs),
      this.orderDataService.isPrimed(),
      this.orderLifecycleRateSubscriptionSource.isPrimed(),
    ]).then((values: any[]) => {
      if (values != null && values.length > 0) {
        this.setCoolOffTime(values[0]);
      }

      this.orderTempMap.clear();

      let time: number = Date.now() - this.orderTempCoolOffSecs * 1000;
      this.orderDataService
        .getBase()
        .getAll()
        .forEach((order: OrderDataImpl) => this.processTempUpdate(order, time));

      this.orderUpdateSubscription = this.orderDataService
        .getModelUpdateSubject()
        .subscribe(this.onOrderUpdateEvent.bind(this));
      this.orderDeleteSubscription = this.orderDataService
        .getModelDeleteSubject()
        .subscribe(this.onOrderDeleteEvent.bind(this));
      this.rateUpdateSubscription = this.rateService
        .getRateEventSubject()
        .subscribe(this.onRateUpdate.bind(this));
    });
  }

  protected destroy(): void {
    [this.orderUpdateSubscription, this.orderDeleteSubscription, this.rateUpdateSubscription]
      .filter((value: Subscription) => value != null)
      .forEach((subscription: Subscription) => {
        try {
          subscription.unsubscribe();
        } catch (e) {
          logger.out.warn(this.constructor.name + ": Failed to close all subscriptions.");
        }
      });

    this.orderTempMap.clear();
  }

  protected onOrderUpdateEvent(order: OrderDataImpl): void {
    if (this.notificationEnabled) {
      this.processTempUpdate(order);
    }
  }

  protected onOrderDeleteEvent(orderId: string): void {
    this.orderTempMap.delete(orderId);
  }

  protected onRateUpdate(rates: Rate[]): void {
    if (this.notificationEnabled) {
      let orderDataLookupService: OrderDataLookupService =
        this.orderDataService.getBase() as OrderDataLookupService;
      rates
        .map((rate: Rate) => rate.productCode)
        .filter(
          (productCode: string, index: number, self: Array<string>) =>
            self.indexOf(productCode) === index,
        )
        .forEach((pair: string) => {
          let orders: OrderDataImpl[] = [
            ...orderDataLookupService.getActiveOrderIdsByAssetCode(pair),
          ].map((orderId: string) => orderDataLookupService.getItem(orderId));
          orders.forEach((order: OrderDataImpl) => this.processTempUpdate(order));
        });
    }
  }

  protected checkNotificationEnabled(): void {
    let shouldNotify: boolean = this.notifService.shouldNotify(
      BFXNotificationType.HOT,
      OrderType.LIMIT_TP,
    );
    if (this.notificationEnabled != shouldNotify) {
      logger.out.info(this.constructor.name + ": Hot notification change : " + shouldNotify);

      if (shouldNotify) {
        this.initialize();
      } else {
        this.destroy();
      }

      this.notificationEnabled = shouldNotify;
    }
  }

  protected processTempUpdate(order: OrderDataImpl, time?: number): void {
    if (
      (order.orderState == OrderState.ACTIVE || order.orderState == OrderState.TRIGGERED) &&
      order.trackingRt != null
    ) {
      let orderId: string = null;
      try {
        orderId = order.orderId.toString();
        let temperature: Temperature = this.percentageAwayHelper.calculateTemperature(order);
        if (temperature != null) {
          this.assesHotForNotif(
            orderId,
            temperature,
            time != null ? time : Date.now(),
            order,
            time != null,
          );
        }
      } catch (e) {
        logger.out.warn(this.constructor.name + ": Failed to read temperature on order " + orderId);
      }
    }
  }

  protected assesHotForNotif(
    orderId: string,
    temp: Temperature,
    time: number,
    itemData: OrderDataImpl,
    skipNotificationFire: boolean = false,
  ) {
    itemData = itemData == null ? this.orderDataService.getBase().getItem(orderId) : itemData;

    if (
      (itemData != null && itemData.orderState === OrderState.ACTIVE) ||
      itemData.orderState === OrderState.TRIGGERED
    ) {
      if (temp === Temperature.Hot) {
        let sendNotif: boolean = false;
        let currentTemp = this.orderTempMap.get(orderId);
        if (currentTemp) {
          if (
            (currentTemp.temp === Temperature.Cold || currentTemp.temp === Temperature.Warm) &&
            (!currentTemp.lastHot ||
              currentTemp.lastHot < Date.now() - this.orderTempCoolOffSecs * 1000)
          ) {
            sendNotif = true;
            this.orderTempMap.set(orderId, { temp: temp, lastHot: time });
          }
        } else {
          sendNotif = true;
          this.orderTempMap.set(orderId, { temp: temp, lastHot: time });
        }

        if (sendNotif && !skipNotificationFire) {
          logger.out.info(this.constructor.name + ": Order became hot " + orderId);
          this.notifService.fireNotificationEvent(
            this.notifService.orderDataToNotification(
              itemData as OrderDataImpl,
              BFXNotificationType.HOT,
            ),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
        }
      } else {
        let currentTemp = this.orderTempMap.get(orderId);
        if (currentTemp) {
          currentTemp.temp = temp;
        } else {
          this.orderTempMap.set(orderId, { temp: temp, lastHot: undefined });
        }
      }
    }
  }
}
